import { Link } from 'react-router-dom';
import LegalPage from '../components/LegalPage';
import { LEGAL_ENTITY_LONG, SUPPORT_EMAIL, GOVERNING_LAW } from '../legalInfo';

const SECTIONS = [
  {
    id: 'agreement',
    heading: 'Agreement to these terms',
    body: (
      <>
        <p>
          These Terms of Service (the &ldquo;Terms&rdquo;) are an agreement between you and{' '}
          {LEGAL_ENTITY_LONG} (&ldquo;Mermadic&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;)
          covering your use of the Mermadic website, dashboard, APIs, container registry, and
          related services (together, the &ldquo;Service&rdquo;).
        </p>
        <p>
          By creating an account or using the Service, you agree to these Terms. If you are
          agreeing on behalf of a company, you confirm you have the authority to bind that
          company. If you do not agree, do not use the Service.
        </p>
      </>
    ),
  },
  {
    id: 'account',
    heading: 'Your account and API keys',
    body: (
      <>
        <ul>
          <li>You must be at least 18 years old and give us accurate registration details.</li>
          <li>
            You are responsible for everything that happens under your account, including
            anything done with your API keys.
          </li>
          <li>
            API keys are secrets. Do not share them, commit them to public repositories, or
            embed them in client-side code. You can rotate or revoke keys at any time from your
            account settings.
          </li>
          <li>
            Tell us at <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> as soon as you
            suspect unauthorized use. We may suspend an account we reasonably believe is
            compromised.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'service',
    heading: 'The Service and plans',
    body: (
      <>
        <p>
          Mermadic runs your container workloads &mdash; one-off jobs, scheduled cron jobs, and
          long-running deployments &mdash; on infrastructure we operate.
        </p>
        <ul>
          <li>
            <strong>Shared</strong> is free to start on shared infrastructure and is billed by
            usage against prepaid credit. Availability is best-effort, and dedicated API,
            private networking, and deployments are not included.
          </li>
          <li>
            <strong>Gold</strong> and <strong>Premium</strong> are dedicated plans billed as a
            monthly subscription plus usage. They include a dedicated cluster, private
            networking, and a dedicated API endpoint. Premium additionally offers opt-in GPU
            jobs.
          </li>
        </ul>
        <p>
          Current plan features, resource limits, and rates are described on our{' '}
          <Link to="/pricing">Pricing</Link> and <Link to="/documentation">Documentation</Link>{' '}
          pages, which form part of these Terms. We may change features and limits over time;
          we will give advance notice of material reductions as described under{' '}
          <a href="#changes">Changes to these terms</a>.
        </p>
      </>
    ),
  },
  {
    id: 'acceptable-use',
    heading: 'Acceptable use',
    body: (
      <>
        <p>You may not use the Service to:</p>
        <ul>
          <li>break the law, infringe intellectual property, or violate someone&rsquo;s privacy;</li>
          <li>
            mine cryptocurrency or run comparable resource-farming workloads, including
            proof-of-work, airdrop and faucet farming, and similar activity;
          </li>
          <li>
            distribute malware, operate botnets or command-and-control infrastructure, run
            phishing campaigns, or send unsolicited bulk email;
          </li>
          <li>
            scan, probe, attack, or attempt to gain unauthorized access to any network or
            system, including ours;
          </li>
          <li>
            attempt to escape container isolation, reach other customers&rsquo; data, or
            circumvent quotas, metering, resource limits, or free-tier restrictions;
          </li>
          <li>
            resell the Service as a general-purpose compute offering without our written
            agreement;
          </li>
          <li>
            run workloads that create outsized risk to our platform or to other customers.
          </li>
        </ul>
        <p>
          You are responsible for what your workloads do, including any outbound network traffic
          they generate.
        </p>
        <p>
          We may suspend or remove workloads immediately and without prior notice when we
          reasonably believe they violate this section, threaten platform stability or security,
          or expose us to legal risk. We will tell you as soon as practical and, where the issue
          can be fixed, give you a chance to fix it.
        </p>
      </>
    ),
  },
  {
    id: 'your-content',
    heading: 'Your content',
    body: (
      <>
        <p>
          You keep all rights to your container images, code, configuration, and data
          (&ldquo;Your Content&rdquo;). You grant us a limited license to host, copy, transmit,
          and run Your Content solely to provide, secure, and support the Service.
        </p>
        <p>
          You confirm you have the rights you need for Your Content, including any third-party
          base images and dependencies you use.
        </p>
      </>
    ),
  },
  {
    id: 'our-ip',
    heading: 'Our intellectual property and your feedback',
    body: (
      <>
        <p>
          The Service itself &mdash; the platform, dashboard, APIs, documentation, and the
          Mermadic name and logo &mdash; belongs to us or our licensors and is protected by
          intellectual property law. We grant you a limited, non-exclusive, non-transferable
          right to use the Service under these Terms while your account is in good standing. We
          keep every right we do not expressly grant you.
        </p>
        <p>
          You may not copy, resell, reverse engineer, or create a competing service from the
          Service or its documentation, except where that restriction is prohibited by law.
        </p>
        <p>
          If you send us feedback, feature requests, or suggestions, we may use them freely to
          improve the Service, with no obligation or payment to you. You keep any rights you
          already hold; nothing here transfers ownership of Your Content.
        </p>
      </>
    ),
  },
  {
    id: 'billing',
    heading: 'Fees, credit, and billing',
    body: (
      <>
        <ul>
          <li>
            <strong>Usage charges.</strong> Jobs, GPU jobs, and deployments are metered as
            described on the <Link to="/pricing">Pricing</Link> page. Metering starts when your
            container starts, not when a job is queued.
          </li>
          <li>
            <strong>Shared.</strong> You prepay credit and usage draws it down. Jobs stop
            starting once your credit runs out.
          </li>
          <li>
            <strong>Dedicated.</strong> The monthly subscription fee is charged in advance.
            Usage is charged when your outstanding balance reaches the threshold shown in your
            account and at the end of a billing period.
          </li>
          <li>
            Payments are processed by Stripe. You authorize us to charge your payment method for
            all amounts due. Fees exclude taxes, which are your responsibility.
          </li>
          <li>
            <strong>Failed payment.</strong> We will retry and notify you. If an invoice stays
            unpaid past the grace period shown in your account, we may suspend the Service and,
            after continued nonpayment, deactivate and delete the associated cluster and its
            data.
          </li>
          <li>
            <strong>Spending limits.</strong> To protect you and us from runaway cost, we may
            set or adjust a spending limit on an account. We will email you when we do, and
            workloads may be prevented from starting once a limit is reached.
          </li>
          <li>
            <strong>Billing corrections.</strong> Published rates and metered usage can contain
            errors. We may correct a billing mistake even after an invoice has been issued or
            paid, in either direction &mdash; refunding you if we overcharged, or invoicing the
            difference if we undercharged. We will explain any correction before we charge it.
          </li>
          <li>
            We may change prices with at least 30 days&rsquo; notice by email or in-product.
            Changes take effect at your next billing period.
          </li>
          <li>
            Charges are non-refundable except where required by law or where we agree in
            writing. Prepaid credit is non-transferable and has no cash value.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'termination',
    heading: 'Cancellation and termination',
    body: (
      <>
        <ul>
          <li>
            You can cancel a dedicated plan at any time with 30 days&rsquo; notice. Deleting your
            cluster stops the recurring infrastructure fee going forward; usage already incurred
            is still payable.
          </li>
          <li>You can close your account by contacting us.</li>
          <li>
            We may suspend or terminate for material breach (including{' '}
            <a href="#acceptable-use">Acceptable use</a>), nonpayment, or legal requirement. For
            non-urgent breaches we will give notice and a reasonable chance to cure.
          </li>
          <li>
            On termination we stop the Service and delete your clusters, workloads, registry
            images, and stored data. Export anything you need first &mdash; see{' '}
            <a href="#availability">Availability, support, and backups</a>.
          </li>
        </ul>
        <p>
          Terms that by their nature should survive &mdash; fees owed, disclaimers, limitation
          of liability, indemnification, and governing law &mdash; survive termination.
        </p>
      </>
    ),
  },
  {
    id: 'availability',
    heading: 'Availability, support, and backups',
    body: (
      <>
        <ul>
          <li>
            We work to keep the Service running and publish target availability by plan on our{' '}
            <Link to="/pricing">Pricing</Link> page. Unless we have signed a separate written
            service level agreement with you, those targets are goals rather than guarantees and
            do not create service credits.
          </li>
          <li>
            We perform maintenance as needed. We try to schedule disruptive work outside peak
            hours and give notice where practical.
          </li>
          <li>
            Support is available by email at{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> and through our{' '}
            <Link to="/contact">contact form</Link>. Response targets vary by plan.
          </li>
        </ul>
        <div className="legal-callout">
          <p>
            <strong>Backups are your responsibility.</strong> We may keep operational backups for
            our own resilience, but we do not promise to retain, restore, or recover Your
            Content, job logs, or registry images. Keep your own copies of anything you cannot
            afford to lose.
          </p>
        </div>
      </>
    ),
  },
  {
    id: 'restricted-data',
    heading: 'Restricted data',
    body: (
      <>
        <p>
          The Service is general-purpose compute. Unless we have agreed otherwise in writing, you
          must not use it to process data subject to special regulatory regimes &mdash; including
          protected health information under HIPAA, cardholder data beyond what Stripe handles on
          our behalf, government classified information, or data that requires a specific
          compliance certification. We do not act as your HIPAA business associate.
        </p>
      </>
    ),
  },
  {
    id: 'third-parties',
    heading: 'Third-party services',
    body: (
      <>
        <p>
          The Service runs on and integrates with third parties, including Amazon Web Services
          for infrastructure, Stripe for payments, GitHub for source integration, and any
          container registries you choose to pull images from. Their terms govern their services,
          and we are not responsible for them.
        </p>
      </>
    ),
  },
  {
    id: 'disclaimers',
    heading: 'Disclaimers',
    body: (
      <>
        <p>
          Except as expressly stated in these Terms, the Service is provided &ldquo;as is&rdquo;
          and &ldquo;as available&rdquo; without warranties of any kind, whether express or
          implied, including implied warranties of merchantability, fitness for a particular
          purpose, and non-infringement, and any warranty that the Service will be
          uninterrupted, error-free, or completely secure. Where a jurisdiction does not allow
          these exclusions, they apply to the greatest extent permitted.
        </p>
      </>
    ),
  },
  {
    id: 'liability',
    heading: 'Limitation of liability',
    body: (
      <>
        <p>To the maximum extent permitted by law:</p>
        <ul>
          <li>
            Neither party is liable for indirect, incidental, special, consequential, or punitive
            damages, or for lost profits, revenue, goodwill, or data.
          </li>
          <li>
            Our total aggregate liability arising out of or relating to the Service is limited to
            the amounts you paid us in the 12 months before the event giving rise to the claim,
            or $100 if you paid us nothing.
          </li>
        </ul>
        <p>
          These limits do not apply to your payment obligations or to liability that cannot be
          limited by law.
        </p>
      </>
    ),
  },
  {
    id: 'indemnification',
    heading: 'Indemnification',
    body: (
      <>
        <p>
          You will defend and indemnify Mermadic against third-party claims arising from Your
          Content, from your workloads, or from your breach of these Terms, and will pay damages
          and reasonable costs finally awarded or agreed in settlement.
        </p>
      </>
    ),
  },
  {
    id: 'changes',
    heading: 'Changes to these terms',
    body: (
      <>
        <p>
          We may update these Terms. For material changes we will give at least 30 days&rsquo;
          notice by email or in-product before they take effect. Continuing to use the Service
          after that date means you accept the updated Terms. If you do not accept them, stop
          using the Service and cancel your plan.
        </p>
      </>
    ),
  },
  {
    id: 'general',
    heading: 'General',
    body: (
      <>
        <ul>
          <li>
            <strong>Governing law.</strong> These Terms are governed by the laws of{' '}
            {GOVERNING_LAW}, without regard to conflict-of-law rules, and the courts located
            there have exclusive jurisdiction.
          </li>
          <li>
            <strong>Assignment.</strong> You may not assign these Terms without our consent. We
            may assign them in connection with a merger, acquisition, or sale of assets.
          </li>
          <li>
            <strong>Entire agreement.</strong> These Terms, together with the Pricing and
            Documentation pages and our <Link to="/privacy">Privacy Policy</Link>, are the entire
            agreement between us. If any provision is unenforceable, the rest stays in effect.
          </li>
          <li>
            <strong>No waiver.</strong> Delay in enforcing a right does not waive it.
          </li>
          <li>
            <strong>Force majeure.</strong> Neither party is liable for delays caused by events
            beyond its reasonable control.
          </li>
          <li>
            <strong>Notices.</strong> We will contact you at your account email address. You can
            reach us at <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
          </li>
          <li>
            <strong>Electronic communications.</strong> You agree that notices, agreements, and
            disclosures we send electronically satisfy any requirement that they be in writing,
            and that clicking to accept counts as your signature.
          </li>
        </ul>
      </>
    ),
  },
];

export default function Terms() {
  return (
    <LegalPage
      title="Terms of Service"
      subtitle="The rules for using Mermadic — what we provide, what we charge, and what we each agree to."
      sections={SECTIONS}
    />
  );
}
